/**
 * The Registration tab (M6 plan P33): a `StationServicePanel` at the Charter Hub (corp.json
 * `founding.registrationTemplate`), mounted in M2's station screen through the M3 bridge like P27's fleet panels.
 * Before founding it is the founding form (fee, name, ticker, logo, livery); after, the Corporation screen (treasury,
 * P&L, transfers, claims, milestones, livery). Commands go through the `fleet` service's `run` (the wallet and
 * `leaseOwned` are filled in from the Career and the `walletDelta` applied once). The form's state lives with the
 * panel, so it survives switching tabs. UI events only.
 *
 * M6 P34: the Corporation screen's *Stations* button opens the station pages (`stationsHost.tsx`: the Stations list
 * with the claims' tariff and docking rights, charters and commissioning, one station's management) in this tab.
 */
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import type { FleetService } from '../../fleet-link/service'
import type { StoryService } from '../../story-link/service'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { StrategicClient } from '../../strategic/client'
import type { CorpView, LiveryColors, LogoRecipe, MeasureName } from '../../strategic/types'
import { errorText } from '../fleet/models/outcome'
import { standingWith } from '../fleet/panels'
import { mountReact } from '../strategic/mount'
import { factionName } from '../strategic/names'
import { ballotRows, cycleBallot, type BallotRow } from './ballotModel'
import { Corporation } from './Corporation'
import { defaultLivery, foundingModel, liveryValid, normalTicker, REGISTRATION_ORDER, TRANSFER_STEPS, type TransferDirection } from './corpModel'
import { Founding } from './Founding'
import { humanContribution, pickDraft } from './logoKit'
import { createScreenScroll, screenKey } from './screenScroll'
import { createStationsHost } from './stationsHost'
import './corp.css'

export type RegistrationFleet = Pick<FleetService, 'run' | 'ownershipUnlocked'>

export interface RegistrationDeps {
  fleet(): RegistrationFleet | null
  client: Pick<StrategicClient, 'corpView' | 'systemView' | 'commissionView' | 'playerStationViews' | 'captainView'>
  career(): Career | null
  /** corp.json `founding.registrationTemplate` (the Charter Hub). */
  registrationTemplate: string
  /** The logo kit seed for this game's drafts. */
  logoSeed: number
  /** Paints the `player_corp` look with the corporation's livery (render `setCorpLivery`). */
  applyLivery(livery: LiveryColors): void
  /** Opens the Charter Board (the claims link). */
  openBoard(): void
  systemName(id: string): string
  /** M7 S39: the story service (the tetherstone ballot toggle writes through it); absent or null: no toggle. */
  story?(): Pick<StoryService, 'apply'> | null
}

const REFUSALS: Readonly<Record<string, string>> = {
  alreadyFounded: 'Your corporation is already registered.',
  invalid: 'The name or ticker is not valid.',
  tickerTaken: 'That ticker belongs to another faction.',
  noOwnership: 'Registration opens once you own your ship’s lease outright.',
  notFounded: 'Register a corporation first.',
}

/** The line for a refused corp command. */
export function corpRefusal(code: string): string {
  return REFUSALS[code] ?? errorText(code)
}

/** True when station `id` of this system is a registration station (the Charter Hub template). */
export function isRegistrationStation(client: Pick<StrategicClient, 'systemView'>, id: string, template: string): boolean {
  const v = client.systemView
  if (v === null) return false
  for (const s of v.stations) if (s.id === id) return s.template === template
  return false
}

export function createRegistrationPanel(deps: RegistrationDeps): StationServicePanel {
  // The form, kept between mounts.
  let name = ''
  let ticker = ''
  let logo: LogoRecipe = pickDraft(deps.logoSeed, 0)
  let livery: LiveryColors = defaultLivery()
  let amount = TRANSFER_STEPS[0] ?? 1000
  let liveryTouched = false

  return {
    id: 'registration',
    title: 'Registration',
    order: REGISTRATION_ORDER,
    available: (site) => deps.fleet() !== null && isRegistrationStation(deps.client, site.id, deps.registrationTemplate),
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const mount = mountReact(el, 'sui-mount')
      let view: CorpView | null = null
      let status: string | null = null
      let busy = false
      let alive = true
      const shown = createScreenScroll()
      const stations = createStationsHost({
        fleet: deps.fleet,
        client: deps.client,
        systemName: deps.systemName,
        holderName: (h) => (h === null ? 'Unclaimed' : h === 'player' ? 'Your claim' : `Held by ${factionName(h)}`),
        standing: (h) => {
          const career = deps.career()
          return career === null || h === null || h === 'player' ? 0 : standingWith(career, h)
        },
        render: () => render(),
        close: () => void reload(),
      })

      function render(): void {
        if (!alive) return
        const career = deps.career()
        const wallet = career?.wallet ?? 0
        if (view === null) {
          mount.render(
            <div className="sui fp-panel" data-testid="corp-loading">
              <p className="sui-empty">Reading the registry…</p>
            </div>,
          )
        } else if (!view.founded) {
          const fleet = deps.fleet()
          const model = foundingModel({
            founded: false,
            leaseOwned: fleet?.ownershipUnlocked() ?? false,
            wallet,
            name,
            ticker,
          })
          mount.render(
            <Founding
              model={model}
              name={name}
              ticker={ticker}
              logo={logo}
              livery={livery}
              busy={busy}
              status={status}
              onName={(v) => {
                name = v
                render()
              }}
              onTicker={(v) => {
                ticker = normalTicker(v).slice(0, 4)
                render()
              }}
              onLogo={(v) => {
                logo = v
                render()
              }}
              onLivery={(v) => {
                livery = v
                render()
              }}
              onFound={found}
            />,
          )
        } else if (stations.page !== null) {
          mount.render(stations.node())
        } else {
          const v = view
          if (!liveryTouched) livery = v.livery
          mount.render(
            <Corporation
              view={v}
              wallet={wallet}
              amount={amount}
              livery={livery}
              busy={busy}
              status={status}
              onAmount={(a) => {
                amount = Number.isFinite(a) ? a : 0
                render()
              }}
              onTransfer={transfer}
              onLivery={(l) => {
                livery = l
                liveryTouched = true
                render()
              }}
              onApplyLivery={repaint}
              onStations={() => stations.open({ kind: 'stations' })}
              onOpenBoard={() => {
                ctx.close()
                deps.openBoard()
              }}
              systemName={deps.systemName}
              ballot={ballot(career, v.founded)}
            />,
          )
        }
        shown(screenKey(view, stations.page), mount.el)
        ctx.refresh()
      }

      /** M7 S39: the ballot toggle, with a story service, a corporation and a first finale commit. */
      function ballot(career: Career | null, founded: boolean): { rows: readonly BallotRow[]; onCycle(m: MeasureName): void } | null {
        const story = deps.story?.() ?? null
        const rows = story === null ? null : ballotRows(career, founded)
        if (rows === null) return null
        return {
          rows,
          onCycle: (m) => {
            if (cycleBallot(deps.story?.() ?? null, deps.career(), m)) render()
          },
        }
      }

      async function reload(): Promise<void> {
        try {
          view = await deps.client.corpView()
        } catch (err: unknown) {
          logger.warn('registration: could not read the corporation view', err)
        }
        render()
      }

      function act(fn: (fleet: RegistrationFleet) => Promise<string>): void {
        const fleet = deps.fleet()
        if (busy) return
        if (fleet === null) {
          status = errorText('noFleet')
          render()
          return
        }
        busy = true
        render()
        void fn(fleet)
          .catch((err: unknown) => corpRefusal(err instanceof Error ? err.message : String(err)))
          .then(async (text) => {
            status = text
            await reload()
          })
          .finally(() => {
            busy = false
            render()
          })
      }

      function found(): void {
        const t = normalTicker(ticker)
        const n = name.trim()
        const recipe = logo
        const colors = livery
        act(async (fleet) => {
          if (!liveryValid(colors)) return 'Pick the livery colours as #rrggbb.'
          const r = await fleet.run({
            type: 'foundCorp',
            name: n,
            ticker: t,
            livery: colors,
            logo: recipe,
          })
          if (!r.ok) return corpRefusal(r.error)
          deps.applyLivery(colors)
          logger.info(`corp: founded ${n} [${t}]; logo humanContribution: ${humanContribution(recipe)}`)
          return `${n} [${t}] is registered.`
        })
      }

      function transfer(direction: TransferDirection): void {
        const a = amount
        act(async (fleet) => {
          const r = await fleet.run({
            type: 'companyTransfer',
            amount: a,
            direction,
          })
          if (!r.ok) return corpRefusal(r.error)
          return direction === 'toCompany' ? 'Moved to the company account.' : 'Moved to your wallet.'
        })
      }

      function repaint(): void {
        const colors = livery
        act(async (fleet) => {
          if (!liveryValid(colors)) return 'Pick the livery colours as #rrggbb.'
          const r = await fleet.run({ type: 'setLivery', livery: colors })
          if (!r.ok) return corpRefusal(r.error)
          deps.applyLivery(colors)
          liveryTouched = false
          return 'The fleet is repainted.'
        })
      }

      render()
      void reload()
      return (): void => {
        alive = false
        mount.unmount()
      }
    },
  }
}
